<script setup>
import { Link } from '@inertiajs/vue3';

defineOptions({ inheritAttrs: false });

defineProps({
    as: {
        type: String,
        default: 'button',
    },
    color: {
        type: String,
        default: 'lunar',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    fullWidth: {
        type: Boolean,
        default: false,
    },
    href: {
        type: String,
        default: '#',
    },
    size: {
        type: String,
        default: 'md',
    },
    type: {
        type: String,
        default: 'submit',
    },
    duskAttribute: {
        type: String,
        default: '',
    },
    loading: {
        type: Boolean,
        default: false,
    },
    loadingText: {
        type: String,
        default: 'Loading...'
    }
});

// Emit both a semantic custom event (backwards compat) and standard click so parent listeners work without warnings
const emit = defineEmits(['saturnButtonClicked', 'click'])

const colors = {
    ignition: 'bg-ignition hover:bg-dark-ignition active:bg-dark-ignition text-void',
    lunar: 'bg-lunar hover:bg-dark-lunar active:bg-dark-lunar text-void',
    qb: 'bg-qb hover:bg-dark-qb active:bg-dark-qb text-white',
    saturn_gray: 'bg-saturn-gray hover:bg-light-lunar active:bg-light-lunar text-void',
    saturn_purple: 'bg-saturn-purple hover:bg-saturn-purple-dark active:bg-saturn-purple-dark text-white',
    sky: 'bg-sky hover:bg-dark-sky active:bg-dark-sky text-white',
    sun: 'bg-sun hover:bg-dark-sun active:bg-dark-sun text-void',
    thruster: 'bg-thruster hover:bg-dark-thruster active:bg-dark-thruster text-void',
    none: '',
};

const sizes = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-4 py-1 text-base',
    md: 'px-4 py-1.5 text-xl',
    lg: 'px-6 py-3 text-[2rem]',
    xl: 'px-4 py-12 text-[2rem]',
};

const baseStyles = 'inline-flex items-center justify-center rounded font-bold transition ease-in-out duration-150 whitespace-nowrap';
const disabledStyles = 'opacity-50 cursor-not-allowed';

</script>

<template>
    <button
        v-if="as === 'button'"
        v-bind="$attrs"
        :dusk="duskAttribute"
        :type="type"
        :disabled="disabled || loading"
        :class="[colors[color], sizes[size], fullWidth && 'w-full', baseStyles, (disabled || loading) ? disabledStyles : 'cursor-pointer']"
        @click.prevent="(e) => { emit('saturnButtonClicked', e); emit('click', e); }"
    >
        <span v-if="loading">{{ loadingText }}</span>
        <slot v-else />
    </button>

    <Link
        v-if="as === 'a'"
        v-bind="$attrs"
        :href="href"
        :dusk="duskAttribute"
        :class="[colors[color], sizes[size], fullWidth && 'w-full', baseStyles]"
    >
        <slot />
    </Link>
</template>
